// eslint-disable-next-line no-redeclare, @typescript-eslint/no-redeclare
import { Divider, Group, NumberInput, Skeleton, Space, Stack, Text } from '@mantine/core';
import { useContext, useMemo, useState } from 'react';
import { useElementSize } from '@mantine/hooks';
import { useDrop } from 'react-dnd-latest';
import BookitupSegmentedControl from '../../../Atoms/BookitupSegmentedControl';
import { DocumentTemplateContext } from '../../../Service/Context/DocumentTemplateContext';
import { ProfileContext } from '../../../Service/Context/ProfileContext';
import { DataS } from '../../../Service/DataS';
import { DragItem, PositionDragItem, TaxType } from '../../../Service/DocumentT';
import PositionHeader from '../../Document/Position/PositionHeader';
import { RichInput } from '../../../Organismns/tiptap/TiptapEditorInput';
import PositionGroups from '../../Document/Position/PositionGroups';
import DocumentPosition from '../../Document/Position/DocumentPositionV2';
import DropArea from '../../../Atoms/DropArea';
import DocumentSum from '../../Document/DocumentSum';
import SkeletonItems, { EmptyPositionsSkeleton } from '../../../Atoms/SkeletonItems';
import { openAddProductModal } from '../../Document/Position/AddProductModal';
import DocumentBubbleMenu from '../../../Organismns/tiptap/toolbars/BubbleMenu';
import { usePositionProducts } from '../../../Service/hooks/usePositionProducts';
import { ThemeText } from '../../../Atoms/ThemeText';
import { LicenceS } from '../../../Service/LicenceS';
import { ViolationContext } from '../../../Service/Context/ViolationContext';
import BookitupNumberInput from '../../../Atoms/inputs/BookitupNumberInput';
import DocumentLanguageS from '../../../Service/DocumentLanguageS';
import LinkText from '../../../Atoms/LinkText';
import { WindowContext } from '../../../Service/Context/WindowContext';

const DocumentTemplateSkeleton = () => {
  const { ref, width } = useElementSize();
  const { profile } = useContext(ProfileContext);
  const { sm: isMobile } = useContext(WindowContext);
  const [addingPosition, setAddingPosition] = useState(false);
  const { products } = usePositionProducts();
  const { executeWithLicenceCheck } = useContext(ViolationContext);
  const { loading, bookitupDocument, savePatch, positionHandlers, groupHandlers, refresh } =
    useContext(DocumentTemplateContext);
  const { addGroup } = groupHandlers;
  const { addPosition, removePositionFromGroup, addProducts } = positionHandlers;

  const [{ isOver, canDrop }, drop] = useDrop(
    () => ({
      accept: DragItem.POSITION,
      canDrop: (item: PositionDragItem) => item.position.groupId !== null,
      collect: (monitor) => ({
        isOver: monitor.isOver(),
        canDrop: !!monitor.canDrop(),
      }),
      drop: (item: PositionDragItem) => removePositionFromGroup(item.position.id),
    }),
    [removePositionFromGroup],
  );

  const addProduct = () =>
    executeWithLicenceCheck(LicenceS.Restrictions.PRODUCT_USAGE, () => {
      openAddProductModal({ products, onFinish: (p) => addProducts(p) });
    });

  const addPositionHandler = async () => {
    setAddingPosition(true);
    await addPosition();
    setAddingPosition(false);
  };

  const { documentType, taxType, positions, currencySymbol, language } = bookitupDocument ?? {
    positions: [],
  };
  const { smallScaleBusiness } = profile ?? {};
  const emptyGroups = true; // positionGroups.length === 0;
  // const grouplessPositions = positions.filter((position) => position.groupId === null);
  const emptyPositions = positions.length === 0; // grouplessPositions.length === 0;
  const isPartialInvoice = documentType === 'PARTIAL_INVOICE';
  const isReminder = documentType === 'REMINDER';
  const usePreviewVersion = width < 800;

  return (
    <div ref={ref}>
      <Group position="apart" mb="md">
        <ThemeText size="lg" weight="bolder">
          {loading && 'Laden ...'}
          {!loading && documentType && DocumentLanguageS.documentType(language, documentType)}
          {!loading && !documentType && 'Vorlage'}
        </ThemeText>
        {!smallScaleBusiness && (
          <BookitupSegmentedControl
            value={taxType}
            color="blue"
            data={DataS.netGrossOptions}
            radius="md"
            onChange={(val: TaxType) => savePatch({ taxType: val })}
            sx={{ minWidth: 160 }}
          />
        )}
      </Group>
      {bookitupDocument && (
        <RichInput
          label="Kopftext"
          value={bookitupDocument.headRow}
          patchField={(val: string) => savePatch({ headRow: val })}
          menu={<DocumentBubbleMenu />}
        />
      )}
      <Space h="xs" />
      <PositionHeader
        documentVersion
        currencySymbol={currencySymbol}
        desktopVersion={!usePreviewVersion}
        showDiscount={!isPartialInvoice}
        isPartialInvouce={documentType === 'PARTIAL_INVOICE'}
        language={bookitupDocument?.language}
        sticky={!isMobile}
      />
      <Stack mt={5}>
        {/* <PositionGroups usePreviewVersion={usePreviewVersion} /> */}
        {loading && <SkeletonItems height={100} />}
        {!loading && !addingPosition && emptyPositions && emptyGroups && <EmptyPositionsSkeleton />}
        {bookitupDocument &&
          positions.map((position) => (
            <DocumentPosition
              key={position.id}
              bookitupDocument={bookitupDocument}
              position={position}
              handlers={positionHandlers}
              refreshDocument={refresh}
              previewVersion={usePreviewVersion}
              products={products}
            />
          ))}
      </Stack>
      {/* <div ref={drop} style={{ marginBottom: 10 }}>
        {canDrop && <DropArea isOver={isOver} />}
      </div> */}
      {addingPosition && <Skeleton mt="sm" width="100%" height={100} />}
      {!isReminder && !canDrop && !addingPosition && !loading && (
        <Group mt="md" mb="md">
          {!isPartialInvoice && emptyPositions && (
            <>
              <LinkText onClick={addPositionHandler}>+ Position hinzufügen</LinkText>
              <LinkText onClick={addProduct}>+ Produkt hinzufügen</LinkText>
            </>
          )}
          {/* {emptyGroups && (
            <Text color="blue" variant="link" sx={{ cursor: 'pointer' }} onClick={() => addGroup()}>
              + Gruppe hinzufügen
            </Text>
          )} */}
        </Group>
      )}
      {bookitupDocument && <DocumentSum baseDocument={bookitupDocument} savePatch={savePatch} />}
      <Space h="xl" />
      {bookitupDocument && (
        <RichInput
          label="Fußtext"
          value={bookitupDocument.endRow}
          patchField={(val: string) => savePatch({ endRow: val })}
          menu={<DocumentBubbleMenu />}
          styles={{ minHeight: 150 }}
        />
      )}
    </div>
  );
};

export default DocumentTemplateSkeleton;
